import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { convertRawValue } from '@/utils/CustomEnum'

import { ApiTaSf040 } from '~/endpoints/ApiTaSf040'
import { ApiTaLi070 } from '~/endpoints/ApiTaLi070'

export const doStatus = {
  NONE: 0,
  NOT_DONE: 1,
  DONE: 2,
  HAS_REVIEW: 3,
  DOING: 4,
} as const
export type DoStatus = typeof doStatus[keyof typeof doStatus]

// State types

interface SsoState {
  url: string
}
const getEmptySsoState = (): SsoState => ({
  url: '',
})

export interface PieceInfoNode {
  pieceId: string // コマID
  pieceName: string // コマ名称
  pieceTypeId: string // コマ区分ID
  isPieceStatus: boolean // コマ学習活性フラグ
  isResultInfo: boolean // 正誤情報有無
  essentialStudyStatus: DoStatus // 要点学習取組ステータス
  exerciseStatus: DoStatus // 問題演習取組ステータス
}
interface UnitState {
  unitId: string // 配信単位ID
  subunitId: string // 単元ID
  subunitName: string // 単元名称
  piecesInfoList: PieceInfoNode[]
}
const getEmptyUnitState = (): UnitState => ({
  unitId: '',
  subunitId: '',
  subunitName: '',
  piecesInfoList: [],
})

// State action parameter

interface ActionParamsFetchSso {
  // 遷移先コード
  // 'question': 質問相談アプリ
  // 'effort'：努力賞システム
  transition: 'question' | 'effort'
}

interface ActionParamsFetchUnit {
  unitSubunitId: string // 配信単位単元ID
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Unit',
})
export default class Unit extends VuexModule {
  // State

  // Sso
  private sso_: SsoState = getEmptySsoState()

  // Unit
  private unit_: UnitState = getEmptyUnitState()

  // Getters

  get sso(): SsoState {
    return this.sso_
  }

  get unit(): UnitState {
    return this.unit_
  }

  get isSsoValid(): boolean {
    return this.sso_.url !== ''
  }

  get isAnswerAndCommentaryPiece() {
    return (piece: PieceInfoNode) => {
      return piece.pieceTypeId === '11'
    }
  }

  get isFullDigitalPiece() {
    return (piece: PieceInfoNode) => {
      return piece.pieceTypeId === '23'
    }
  }

  get isFullDigitalTeishutsuPiece() {
    return (piece: PieceInfoNode) => {
      return piece.pieceTypeId === '24'
    }
  }

  get pieceById() {
    return (pieceId: string) => {
      const piece = this.unit_.piecesInfoList.find(e => e.pieceId === pieceId)
      if (piece === undefined) {
        throw new Error(`pieceById: piece ${pieceId} is not found`)
      }
      return piece
    }
  }

  // Mutations

  @Mutation
  updateSso(value: SsoState) {
    this.sso_ = value
  }

  @Mutation
  updateUnit(value: UnitState) {
    this.unit_ = value
  }

  // Actions
  @Action
  clear() {
    this.context.commit('updateSso', getEmptySsoState())
    this.context.commit('updateUnit', getEmptyUnitState())
  }

  @Action
  async fetchSso(param: ActionParamsFetchSso) {
    logger.trace('in: fetchSso')

    // API: API_TA_LI_070
    const api = new ApiTaLi070.Api()
    const result = await api.get<SsoState | null>(
      {
        query: {
          transition: param.transition,
        },
      },
      // success
      (data, _) => {
        return {
          url: data.url,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      // エラー時は初期値にクリアする
      this.context.commit('updateSso', getEmptySsoState())
      return
    }
    this.context.commit('updateSso', result)
  }

  @Action
  async fetchUnit(param: ActionParamsFetchUnit) {
    logger.trace('in: fetchUnit')

    // API: API_TA_SF_040
    const api = new ApiTaSf040.Api()
    const result = await api.get<UnitState | null>(
      {
        query: {
          unitSubunitId: param.unitSubunitId,
        },
      },
      // success
      (data, _) => {
        return {
          unitId: data.unitId,
          subunitId: data.subunitId,
          subunitName: data.subunitName,
          // piecesInfoList: data.piecesInfoList,
          piecesInfoList: ((response: ApiTaSf040.Get.Response) =>
            response.piecesInfoList.map(p => ({
              pieceId: p.pieceId,
              pieceName: p.pieceName,
              pieceTypeId: p.pieceTypeId,
              isPieceStatus: p.isPieceStatus,
              isResultInfo: p.isResultInfo,
              essentialStudyStatus: convertRawValue<number, DoStatus>(p.essentialStudyStatus, doStatus),
              exerciseStatus: convertRawValue<number, DoStatus>(p.exerciseStatus, doStatus),
            })))(data),
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateUnit', result)
  }
}
